import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Check, Wand2, X as XIcon, Loader2, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// (voice dictation removed in iter15 — no MicButton import needed)

const DURATION_OPTIONS = [
  { value: "none", label: "No timer" },
  { value: "5", label: "5 min" },
  { value: "15", label: "15 min" },
  { value: "25", label: "25 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "1 hour" },
  { value: "90", label: "1.5 hours" },
  { value: "120", label: "2 hours" },
];

const AUTOSAVE_DELAY = 600;

function buildPayload(form, original) {
  const out = {};
  if (form.title.trim() !== (original.title || "")) out.title = form.title.trim();
  if ((form.description || "") !== (original.description || ""))
    out.description = form.description.trim();
  if (form.priority !== original.priority) out.priority = form.priority;
  if (form.category !== original.category) out.category = form.category;
  if (form.recurrence !== (original.recurrence || "none")) out.recurrence = form.recurrence;

  const newDue = form.due_date ? form.due_date.toISOString() : null;
  if (newDue !== (original.due_date || null)) out.due_date = newDue;

  const newDur =
    form.duration_minutes && form.duration_minutes !== "none"
      ? parseInt(form.duration_minutes, 10)
      : null;
  if (newDur !== (original.duration_minutes ?? null)) out.duration_minutes = newDur;

  return out;
}

export default function EditTaskDialog({ task, open, onClose, onSave, onBreakdown }) {
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [dateOpen, setDateOpen] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const timerRef = useRef(null);
  const originalRef = useRef(null);
  const skipNextAutosave = useRef(true);
  const prevTaskIdRef = useRef(null);

  useEffect(() => {
    if (!task) return;
    if (task.id !== prevTaskIdRef.current) {
      // Different task — full reset.
      prevTaskIdRef.current = task.id;
      originalRef.current = task;
      skipNextAutosave.current = true;
      setForm({
        title: task.title || "",
        description: task.description || "",
        priority: task.priority || "medium",
        category: task.category || "work",
        recurrence: task.recurrence || "none",
        due_date: task.due_date ? new Date(task.due_date) : null,
        duration_minutes: task.duration_minutes ? String(task.duration_minutes) : "none",
      });
      setStatus("idle");
      return;
    }
    // Same task — upstream re-render (typically our own autosave response or a
    // collaborator update). Update the diff baseline so the next save sends the
    // user's true intent, but DO NOT touch the form (user may be mid-keystroke).
    originalRef.current = task;
  }, [task]);

  // Auto-save on form change
  useEffect(() => {
    if (!form || !originalRef.current) return;
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false;
      return;
    }
    if (!form.title.trim()) {
      setStatus("error");
      return;
    }
    setStatus("saving");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const patch = buildPayload(form, originalRef.current);
      if (Object.keys(patch).length === 0) {
        setStatus("saved");
        return;
      }
      try {
        await onSave(patch);
        originalRef.current = { ...originalRef.current, ...patch };
        setStatus("saved");
      } catch {
        setStatus("error");
      }
    }, AUTOSAVE_DELAY);
    return () => clearTimeout(timerRef.current);
  }, [form, onSave]);

  if (!form) return null;
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-none max-w-lg p-0 gap-0 border border-zinc-300"
        data-testid="edit-task-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-200 flex flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">
            Edit task
          </DialogTitle>
          <SaveStatus status={status} />
        </DialogHeader>

        <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
              Title
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              className="w-full h-11 px-3 text-sm border border-zinc-300 focus:border-[#002FA7] outline-none bg-transparent"
              data-testid="edit-title-input"
              autoFocus
            />
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
              Description
              <Wand2 className="w-3 h-3 text-zinc-400" strokeWidth={1.5} />
              <span className="normal-case font-normal tracking-normal text-zinc-400 text-[10px]">
                — helps AI generate better subtasks
              </span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              rows={3}
              placeholder="Optional. Add context, requirements, blockers..."
              className="w-full p-3 text-sm border border-zinc-300 focus:border-[#002FA7] outline-none bg-transparent resize-none"
              data-testid="edit-description-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
                Priority
              </label>
              <Select value={form.priority} onValueChange={(v) => update({ priority: v })}>
                <SelectTrigger className="rounded-none h-11" data-testid="edit-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
                Category
              </label>
              <Select value={form.category} onValueChange={(v) => update({ category: v })}>
                <SelectTrigger className="rounded-none h-11" data-testid="edit-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="work">Work</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
                Due date
              </label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-full h-11 px-3 flex items-center gap-2 text-sm border border-zinc-300 hover:bg-zinc-50"
                    data-testid="edit-due-date"
                  >
                    <CalendarIcon className="w-4 h-4" strokeWidth={1.5} />
                    {form.due_date ? format(form.due_date, "MMM d, yyyy") : "No date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={form.due_date}
                    onSelect={(d) => {
                      update({ due_date: d });
                      setDateOpen(false);
                    }}
                  />
                  {form.due_date && (
                    <div className="p-2 border-t border-zinc-200">
                      <button
                        type="button"
                        onClick={() => update({ due_date: null })}
                        className="text-xs text-zinc-500 hover:text-zinc-900"
                      >
                        Clear date
                      </button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
                Duration
              </label>
              <Select
                value={form.duration_minutes}
                onValueChange={(v) => update({ duration_minutes: v })}
              >
                <SelectTrigger className="rounded-none h-11" data-testid="edit-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {DURATION_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 block">
                Recurrence
              </label>
              <Select
                value={form.recurrence}
                onValueChange={(v) => update({ recurrence: v })}
              >
                <SelectTrigger className="rounded-none h-11" data-testid="edit-recurrence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="none">No repeat</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {task?.subtasks && task.subtasks.length > 0 && (
            <div className="pt-2" data-testid="edit-subtasks-preview">
              <label className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-[#002FA7]" strokeWidth={1.5} />
                Subtasks · {task.subtasks.length}
              </label>
              <ul className="border border-zinc-200 divide-y divide-zinc-200">
                {task.subtasks.slice(0, 8).map((s) => (
                  <li key={s.id} className="px-3 py-2 text-xs text-zinc-700 flex items-center gap-2">
                    <span className={`w-3 h-3 border ${s.completed ? "bg-emerald-600 border-emerald-600" : "border-zinc-400"} inline-flex items-center justify-center shrink-0`}>
                      {s.completed && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className={s.completed ? "line-through text-zinc-400" : ""}>{s.title}</span>
                  </li>
                ))}
                {task.subtasks.length > 8 && (
                  <li className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-zinc-400 italic">
                    + {task.subtasks.length - 8} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-zinc-200">
          {onBreakdown && (
            <button
              type="button"
              onClick={async () => {
                if (breaking) return;
                if (!form.title.trim()) return;
                // Flush any pending autosave so the AI sees the latest title/description.
                clearTimeout(timerRef.current);
                const patch = buildPayload(form, originalRef.current);
                if (Object.keys(patch).length > 0) {
                  try { await onSave(patch); originalRef.current = { ...originalRef.current, ...patch }; setStatus("saved"); } catch {}
                }
                setBreaking(true);
                try { await onBreakdown(); } finally { setBreaking(false); }
              }}
              disabled={breaking || !form.title.trim()}
              className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] px-4 py-2.5 text-[#002FA7] hover:bg-blue-50 border border-blue-200 hover:border-[#002FA7] transition-colors disabled:opacity-50"
              data-testid="edit-breakdown-btn"
              title="Generate AI subtasks"
            >
              <Sparkles className={`w-3.5 h-3.5 ${breaking ? "animate-pulse" : ""}`} strokeWidth={1.5} />
              {breaking ? "Breaking down…" : "Break down with AI"}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] px-4 py-2.5 text-zinc-600 hover:bg-zinc-100 ml-auto"
            data-testid="edit-close"
          >
            <XIcon className="w-3.5 h-3.5" strokeWidth={1.5} />
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SaveStatus({ status }) {
  if (status === "saving") {
    return (
      <span
        className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-zinc-500"
        data-testid="autosave-status"
      >
        <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
        Saving…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span
        className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-emerald-700"
        data-testid="autosave-status"
      >
        <Check className="w-3 h-3" strokeWidth={2.5} />
        Saved
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        className="text-[10px] uppercase tracking-[0.12em] text-red-700"
        data-testid="autosave-status"
      >
        Couldn't save
      </span>
    );
  }
  return <span className="text-[10px] uppercase tracking-[0.12em] text-transparent">—</span>;
}
